import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { SiteMembership } from '@se-po/shared-data-access-models'
import { HouseholdProfilesService, SiteMembershipService, SurveyResultService } from '@se-po/shared-data-access-services'
import {
  OpenRegistrationFilterOption,
  OpenRegistrationFilters,
} from '@se-po/se-registration-ui-feature-open-registration-list'
import { DateTime } from 'luxon'
import { catchError, forkJoin, of, Subscription } from 'rxjs'
import { SeFeTranslationsLoader } from 'se-fe-translations'
import { SurveyResult } from 'se-resource-types/dist/lib/Ngin'

interface CompletedRegistrationPersona {
  id?: number | string
  name?: string
  first_name?: string
  last_name?: string
  avatar_url?: string
  image_url?: string
  profile_image_url?: string
  profile_images?: CompletedRegistrationProfileImage[] | null
}

interface CompletedRegistrationProfileImage {
  image_type?: string
  url?: string
}

interface CompletedRegistrationHouseholdProfile {
  persona?: CompletedRegistrationPersona
}

interface CompletedRegistrationHouseholdProfilesResult {
  profiles?: CompletedRegistrationHouseholdProfile[]
}

interface CompletedRegistrationSurveyResult extends SurveyResult {
  sport?: string
  sport_key?: string
  signup_types?: string
  persona?: CompletedRegistrationPersona
}

interface CompletedRegistrationSiteMembership extends Omit<SiteMembership, 'organization_logo' | 'site_logo_graphic'> {
  organization_logo: CompletedRegistrationOrganizationLogo
  site_logo_graphic: CompletedRegistrationSiteLogo | null
}

interface CompletedRegistrationOrganizationLogo {
  medium_logo_url: string
  url?: string
  large_logo_url?: string
  small_logo_url?: string
  tiny_logo_url?: string
}

interface CompletedRegistrationSiteLogo {
  medium_icon_url?: string
  small_icon_url?: string
  url?: string
  large_url?: string
  tiny_icon_url?: string
}

interface CompletedRegistrationCard {
  id: string
  registrationName: string
  orgName: string
  orgLogoUrl: string
  orgColor: string
  sport: string
  type: string
  avatarUrl: string
  backgroundImgUrl: string
  completedDate: string
  orderRouterLink: string | unknown[]
  orderRouterQueryParams: Record<string, string | number>
  showShareAction: boolean
}

interface CompletedRegistrationFilterIds {
  sport: string
  organization: string
  type: string
  person: string
}

interface CompletedRegistrationFilterOptions {
  sport: OpenRegistrationFilterOption
  organization: OpenRegistrationFilterOption
  type: OpenRegistrationFilterOption
  person: OpenRegistrationFilterOption
}

interface CompletedRegistrationRecord {
  surveyResult: CompletedRegistrationSurveyResult
  filterIds: CompletedRegistrationFilterIds
  filterOptions: CompletedRegistrationFilterOptions
  card: CompletedRegistrationCard
}

type SiteLogoGraphic = {
  medium_icon_url?: string | null
  medium_logo_url?: string | null
  url?: string | null
}

@Component({
  selector: 'se-po-completed-registration-list',
  templateUrl: './completed-registration-list.component.html',
  styleUrls: ['./completed-registration-list.component.scss'],
  standalone: false,
})
export class CompletedRegistrationListComponent implements OnInit, OnDestroy {
  public loaded = false
  public error = false
  public noResults = false
  public surveyResults: CompletedRegistrationSurveyResult[] = []
  public completedRegistrationRecords: CompletedRegistrationRecord[] = []
  public filteredCards: CompletedRegistrationCard[] = []
  public sports: OpenRegistrationFilterOption[] = []
  public organizations: OpenRegistrationFilterOption[] = []
  public signupTypeList: OpenRegistrationFilterOption[] = []
  public people: OpenRegistrationFilterOption[] = []
  public searchTerm = ''
  public filters: OpenRegistrationFilters = this.emptyFilters()
  public readonly translateKey = 'SE_PO_SE_REGISTRATION_UI_FEATURE_COMPLETED_REGISTRATION_LIST.'

  private readonly subscriptions = new Subscription()
  private readonly translationsPath = 'se-registration-ui/feature-completed-registration-list/assets/translations'
  private readonly membershipByOrganizationId = new Map<string, CompletedRegistrationSiteMembership>()
  private readonly membershipBySiteId = new Map<string, CompletedRegistrationSiteMembership>()
  private readonly profilePersonaById = new Map<string, CompletedRegistrationPersona>()
  private initialPersonFilterIds: string[] = []
  private initialPersonFiltersApplied = false

  constructor(
    private surveyResultService: SurveyResultService,
    private siteMembershipService: SiteMembershipService,
    private householdProfilesService: HouseholdProfilesService,
    private translationsLoader: SeFeTranslationsLoader,
    private activatedRoute: ActivatedRoute
  ) {}

  public ngOnInit(): void {
    this.initialPersonFilterIds = this.personFilterIdsFromRoute()
    void this.loadTranslations()
    this.loadCompletedRegistrations()
  }

  private async loadTranslations(): Promise<void> {
    try {
      await this.translationsLoader.load(this.translationsPath)
    } catch {
      // Continue rendering with translation keys as a fallback if the asset request fails.
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe()
  }

  public handleSearchTermChange(searchTerm: string): void {
    this.searchTerm = searchTerm || ''
    this.applyClientFilters()
  }

  public handleFiltersChange(filters: OpenRegistrationFilters): void {
    this.filters = {
      sport: [...(filters?.sport || [])],
      organization: [...(filters?.organization || [])],
      type: [...(filters?.type || [])],
      person: [...(filters?.person || [])],
    }
    this.applyClientFilters()
  }

  public clearFilters(): void {
    this.searchTerm = ''
    this.filters = this.emptyFilters()
    this.applyClientFilters()
  }

  private loadCompletedRegistrations(): void {
    this.loaded = false
    this.error = false
    this.noResults = false
    this.completedRegistrationRecords = []

    this.subscriptions.add(
      forkJoin({
        surveyResults: this.surveyResultService.findMine({ page: 1, per_page: 1000 }),
        siteMemberships: this.siteMembershipService.mine().pipe(
          catchError(() => of([] as SiteMembership[]))
        ),
        householdProfiles: this.householdProfilesService.mine().pipe(
          catchError(() => of({ profiles: [] } as CompletedRegistrationHouseholdProfilesResult))
        ),
      }).subscribe({
        next: ({ surveyResults, siteMemberships, householdProfiles }) => {
          this.surveyResults = surveyResults as CompletedRegistrationSurveyResult[]
          this.setMemberships(siteMemberships)
          this.setProfilePersonas(householdProfiles as CompletedRegistrationHouseholdProfilesResult)
          this.completedRegistrationRecords = this.surveyResults.map((surveyResult) => (
            this.recordFromSurveyResult(surveyResult)
          ))
          this.setFilterOptions()
          this.applyInitialPersonFilters()
          this.applyClientFilters()
          this.loaded = true
        },
        error: () => {
          this.surveyResults = []
          this.completedRegistrationRecords = []
          this.filteredCards = []
          this.sports = []
          this.organizations = []
          this.signupTypeList = []
          this.people = []
          this.membershipByOrganizationId.clear()
          this.membershipBySiteId.clear()
          this.profilePersonaById.clear()
          this.error = true
          this.noResults = false
          this.loaded = true
        },
      })
    )
  }

  private setMemberships(siteMemberships: SiteMembership[]): void {
    this.membershipByOrganizationId.clear()
    this.membershipBySiteId.clear()
    siteMemberships.forEach((membership) => {
      const completedMembership = membership as CompletedRegistrationSiteMembership

      this.membershipByOrganizationId.set(this.toId(completedMembership.organization_id), completedMembership)
      this.membershipBySiteId.set(this.toId(completedMembership.site_id), completedMembership)
    })
  }

  private buildOrganizationLogoUrl(siteMembership: SiteMembership): string {
    const siteLogoGraphic = siteMembership.site_logo_graphic as SiteLogoGraphic | null | undefined
    return (
      siteLogoGraphic?.medium_icon_url ||
      siteLogoGraphic?.medium_logo_url ||
      siteLogoGraphic?.url ||
      siteMembership.organization_logo?.medium_logo_url ||
      ''
    )
  }

  private setProfilePersonas(householdProfiles: CompletedRegistrationHouseholdProfilesResult): void {
    this.profilePersonaById.clear()
    const profiles = householdProfiles.profiles || []

    profiles.forEach((profile) => {
      if (!profile.persona?.id) return

      this.profilePersonaById.set(this.toId(profile.persona.id), profile.persona)
    })
  }

  private setFilterOptions(): void {
    this.sports = this.sortedUniqueOptions(
      this.completedRegistrationRecords.map((record) => record.filterOptions.sport)
    )
    this.organizations = this.sortedUniqueOptions(
      this.completedRegistrationRecords.map((record) => record.filterOptions.organization)
    )
    this.signupTypeList = this.sortedUniqueOptions(
      this.completedRegistrationRecords.map((record) => record.filterOptions.type)
    )
    this.people = this.sortedUniqueOptions(
      this.completedRegistrationRecords.map((record) => record.filterOptions.person)
    )
  }

  private applyInitialPersonFilters(): void {
    if (this.initialPersonFiltersApplied) return

    this.initialPersonFiltersApplied = true
    if (!this.initialPersonFilterIds.length) return

    this.filters = {
      ...this.filters,
      person: [...this.initialPersonFilterIds],
    }
  }

  private personFilterIdsFromRoute(): string[] {
    return this.personFilterIds(
      this.activatedRoute.snapshot.queryParamMap.get('personFilters')
    )
  }

  private personFilterIds(personFilters: string | null): string[] {
    return (personFilters || '')
      .split(',')
      .map((personFilter) => personFilter.trim())
      .filter(Boolean)
  }

  private sortedUniqueOptions(options: OpenRegistrationFilterOption[]): OpenRegistrationFilterOption[] {
    const optionsById = new Map<string, string>()

    options.forEach(({ id, name }) => {
      if (!id || optionsById.has(id)) return

      optionsById.set(id, name || id)
    })

    return Array.from(optionsById.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }))
  }

  private applyClientFilters(): void {
    const normalizedSearchTerm = this.normalizeSearchText(this.searchTerm)

    const matchingRecords = this.completedRegistrationRecords.filter((record) => (
      this.matchesSearch(record.surveyResult, normalizedSearchTerm) && this.matchesFilters(record)
    ))

    this.filteredCards = matchingRecords.map((record) => record.card)
    this.noResults = !this.error && this.surveyResults.length > 0 && this.filteredCards.length === 0
  }

  private matchesSearch(surveyResult: CompletedRegistrationSurveyResult, normalizedSearchTerm: string): boolean {
    return !normalizedSearchTerm ||
      this.normalizeSearchText(surveyResult.survey_name || '').includes(normalizedSearchTerm)
  }

  private matchesFilters(record: CompletedRegistrationRecord): boolean {
    return this.matchesFilterCategory(record.filterIds.sport, this.filters.sport) &&
      this.matchesFilterCategory(record.filterIds.organization, this.filters.organization) &&
      this.matchesFilterCategory(record.filterIds.type, this.filters.type) &&
      this.matchesFilterCategory(record.filterIds.person, this.filters.person)
  }

  private matchesFilterCategory(value: string, selectedValues: string[] = []): boolean {
    return selectedValues.length === 0 || selectedValues.includes(value)
  }

  private recordFromSurveyResult(surveyResult: CompletedRegistrationSurveyResult): CompletedRegistrationRecord {
    const filterOptions = this.filterOptionsFromSurveyResult(surveyResult)
    const filterIds = this.filterIdsFromOptions(filterOptions)

    return {
      surveyResult,
      filterIds,
      filterOptions,
      card: this.cardFromSurveyResult(surveyResult, filterOptions),
    }
  }

  private filterOptionsFromSurveyResult(
    surveyResult: CompletedRegistrationSurveyResult
  ): CompletedRegistrationFilterOptions {
    return {
      sport: {
        id: this.sportId(surveyResult),
        name: this.sportLabel(surveyResult),
      },
      organization: {
        id: this.organizationId(surveyResult),
        name: this.organizationName(surveyResult),
      },
      type: {
        id: this.typeId(surveyResult),
        name: this.typeLabel(surveyResult),
      },
      person: {
        id: this.personId(surveyResult),
        name: this.personLabel(surveyResult),
      },
    }
  }

  private filterIdsFromOptions(filterOptions: CompletedRegistrationFilterOptions): CompletedRegistrationFilterIds {
    return {
      sport: filterOptions.sport.id,
      organization: filterOptions.organization.id,
      type: filterOptions.type.id,
      person: filterOptions.person.id,
    }
  }

  private cardFromSurveyResult(
    surveyResult: CompletedRegistrationSurveyResult,
    filterOptions: CompletedRegistrationFilterOptions
  ): CompletedRegistrationCard {
    return {
      id: this.toId(surveyResult.id),
      registrationName: surveyResult.survey_name || '',
      orgName: filterOptions.organization.name,
      orgLogoUrl: this.organizationLogoUrl(surveyResult),
      orgColor: this.organizationColor(surveyResult),
      sport: filterOptions.sport.name,
      type: filterOptions.type.name,
      avatarUrl: this.avatarUrl(surveyResult),
      backgroundImgUrl: this.organizationLogoUrl(surveyResult),
      completedDate: this.completedDate(surveyResult.placed_at),
      orderRouterLink: this.registrationDetailsRoute(surveyResult),
      orderRouterQueryParams: this.registrationDetailsQueryParams(surveyResult),
      showShareAction: false,
    }
  }

  private sportId(surveyResult: CompletedRegistrationSurveyResult): string {
    return this.toId(surveyResult.sport_key || surveyResult.sport)
  }

  private sportLabel(surveyResult: CompletedRegistrationSurveyResult): string {
    return surveyResult.sport || this.formatDisplayName(surveyResult.sport_key)
  }

  private organizationId(surveyResult: CompletedRegistrationSurveyResult): string {
    return this.toId(surveyResult.organization_id)
  }

  private organizationName(surveyResult: CompletedRegistrationSurveyResult): string {
    const membership = this.membershipForSurveyResult(surveyResult)

    return membership?.organization_name || surveyResult.organization_name || surveyResult.site_name || ''
  }

  private organizationLogoUrl(surveyResult: CompletedRegistrationSurveyResult): string {
    const membership = this.membershipForSurveyResult(surveyResult)
    const organizationLogo = membership?.organization_logo
    const siteLogo = membership?.site_logo_graphic

    return organizationLogo?.medium_logo_url ||
      organizationLogo?.small_logo_url ||
      organizationLogo?.url ||
      organizationLogo?.large_logo_url ||
      organizationLogo?.tiny_logo_url ||
      siteLogo?.medium_icon_url ||
      siteLogo?.small_icon_url ||
      siteLogo?.url ||
      siteLogo?.large_url ||
      siteLogo?.tiny_icon_url ||
      ''
  }

  private organizationColor(surveyResult: CompletedRegistrationSurveyResult): string {
    const membership = this.membershipForSurveyResult(surveyResult)
    return this.normalizeColor(membership?.organization_color)
  }

  private membershipForSurveyResult(
    surveyResult: CompletedRegistrationSurveyResult
  ): CompletedRegistrationSiteMembership | undefined {
    return this.membershipBySiteId.get(this.toId(surveyResult.site_id)) ||
      this.membershipByOrganizationId.get(this.organizationId(surveyResult))
  }

  private typeId(surveyResult: CompletedRegistrationSurveyResult): string {
    return this.toId(surveyResult.signup_types || surveyResult.survey_type)
  }

  private typeLabel(surveyResult: CompletedRegistrationSurveyResult): string {
    if (surveyResult.signup_types) return surveyResult.signup_types

    return this.formatDisplayName(this.typeId(surveyResult))
  }

  private personId(surveyResult: CompletedRegistrationSurveyResult): string {
    return this.toId(surveyResult.persona?.id ?? surveyResult.persona_id)
  }

  private personLabel(surveyResult: CompletedRegistrationSurveyResult): string {
    const persona = surveyResult.persona
    const profilePersona = this.profilePersonaById.get(this.personId(surveyResult))

    return this.personaDisplayName(persona) ||
      this.personaDisplayName(profilePersona) ||
      this.personId(surveyResult)
  }

  private personaDisplayName(persona?: CompletedRegistrationPersona): string {
    if (!persona) return ''

    const fullName = [persona.first_name, persona.last_name]
      .filter(Boolean)
      .join(' ')

    return persona.name || fullName
  }

  private avatarUrl(surveyResult: CompletedRegistrationSurveyResult): string {
    const persona = surveyResult.persona
    const profilePersona = this.profilePersonaById.get(this.personId(surveyResult))

    const imageUrl = persona?.avatar_url ||
      persona?.image_url ||
      persona?.profile_image_url ||
      this.profileImageUrl(persona?.profile_images) ||
      this.profileImageUrl(profilePersona?.profile_images) ||
      ''

    return imageUrl || this.initialsAvatarUrl(this.personaInitials(persona, profilePersona))
  }

  private profileImageUrl(profileImages?: CompletedRegistrationProfileImage[] | null): string {
    if (!Array.isArray(profileImages)) return ''

    const imageTypePriority: Record<string, number> = {
      crop_icon: 0,
      crop: 1,
      original: 2,
    }
    let selectedImageUrl = ''
    let selectedPriority = Number.POSITIVE_INFINITY

    for (const { image_type, url } of profileImages) {
      const priority = image_type ? imageTypePriority[image_type] : undefined
      if (priority === undefined || !url || priority >= selectedPriority) continue

      selectedImageUrl = url
      selectedPriority = priority
      if (selectedPriority === imageTypePriority.crop_icon) break
    }

    return selectedImageUrl
  }

  private personaInitials(
    persona?: CompletedRegistrationPersona,
    profilePersona?: CompletedRegistrationPersona
  ): string {
    const sourcePersona = this.personaWithNameParts(persona) || this.personaWithNameParts(profilePersona)
    if (!sourcePersona) return ''

    const firstName = sourcePersona.first_name?.trim()
    const lastName = sourcePersona.last_name?.trim()

    if (firstName || lastName) {
      return [firstName, lastName]
        .filter(Boolean)
        .map((name) => name.charAt(0).toUpperCase())
        .join('')
    }

    return (sourcePersona.name || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((name) => name.charAt(0).toUpperCase())
      .join('')
  }

  private personaWithNameParts(persona?: CompletedRegistrationPersona): CompletedRegistrationPersona | undefined {
    if (!persona) return undefined

    return persona.first_name || persona.last_name || persona.name ? persona : undefined
  }

  private initialsAvatarUrl(initials: string): string {
    if (!initials) return ''

    const escapedInitials = this.escapeSvgText(initials)
    const svg = [
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 154 154">',
      '<circle cx="77" cy="77" r="77" fill="#1272af"/>',
      '<text x="77" y="84" text-anchor="middle" dominant-baseline="middle" fill="#fff"',
      ` font-family="Figtree, Arial, sans-serif" font-size="52" font-weight="600">${escapedInitials}</text>`,
      '</svg>',
    ].join('')

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
  }

  private escapeSvgText(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }

  private completedDate(placedAt?: string): string {
    const completedAt = placedAt ? DateTime.fromISO(placedAt) : null

    return completedAt?.isValid ? completedAt.toFormat('LLLL d, yyyy') : ''
  }

  private registrationDetailsRoute(surveyResult: CompletedRegistrationSurveyResult): string | unknown[] {
    const registrationId = this.toId(surveyResult.id)
    if (!registrationId) return ''

    return `/user/registrations/${encodeURIComponent(registrationId)}`
  }

  private registrationDetailsQueryParams(
    surveyResult: CompletedRegistrationSurveyResult
  ): Record<string, string | number> {
    const siteId = this.toId(surveyResult.site_id)

    return siteId ? { siteId } : {}
  }

  private formatDisplayName(value?: string): string {
    return this.toId(value)
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  private normalizeColor(color?: string): string {
    const normalizedColor = (color || '').trim()
    if (!normalizedColor) return ''
    if (normalizedColor.startsWith('#') || normalizedColor.startsWith('rgb')) return normalizedColor

    return `#${normalizedColor}`
  }

  private normalizeSearchText(text: string): string {
    return text.trim().toLocaleLowerCase()
  }

  private toId(value?: number | string): string {
    return value === undefined || value === null ? '' : String(value)
  }

  private emptyFilters(): OpenRegistrationFilters {
    return {
      sport: [],
      organization: [],
      type: [],
      person: [],
    }
  }
}
